import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Shield, ShieldCheck, ShieldX, ShieldAlert, Search, RefreshCw, Loader2,
  CheckCircle2, XCircle, Pause, Play, ArrowUpCircle, ArrowDownCircle, Clock, ScrollText, Users,
} from "lucide-react";
import { adminApi } from "@/lib/admin-api";

const STATUS_BADGE = {
  active:           { label: "Active",    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  pending_approval: { label: "Pending",   cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  rejected:         { label: "Rejected",  cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  suspended:        { label: "Suspended", cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
};

const ROLE_BADGE = {
  admin: { label: "Admin", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", icon: ShieldCheck },
  user:  { label: "User",  cls: "bg-slate-700/40 text-slate-300 border-slate-600/30", icon: Users },
};

function ReasonModal({ open, title, prompt, requireReason, onClose, onSubmit, busy }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  const valid = !requireReason || text.trim().length > 0;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-md" data-testid="reason-modal">
        <DialogHeader><DialogTitle className="text-slate-200">{title}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{prompt}</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)}
          placeholder={requireReason ? "Reason (required)" : "Note (optional)"}
          className="bg-slate-900 border-slate-700 text-sm" data-testid="reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300" data-testid="reason-cancel">Cancel</Button>
          <Button onClick={() => onSubmit(text.trim())} disabled={!valid || busy} className="bg-cyan-600 hover:bg-cyan-500" data-testid="reason-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Client-side guard — show 404 page (not "you are not admin") when
  // the user isn't allowed. The server enforces the same rule on every
  // call, so this is purely cosmetic. Don't reveal the route exists.
  const role = user?.role || "user";
  if (!token || role !== "admin") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center" data-testid="admin-404">
          <h1 className="text-6xl font-bold text-slate-700">404</h1>
          <p className="text-sm text-slate-500 mt-4">Page not found</p>
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mt-4 text-slate-400">
            ← Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  // Reason modal state
  const [modal, setModal] = useState(null); // { user, action }

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, u, a] = await Promise.all([
        adminApi.listPending(),
        adminApi.listUsers({ status: statusFilter || undefined, search: search || undefined, limit: 100 }),
        adminApi.audit({ limit: 50 }),
      ]);
      setPending(p.data.pending || []);
      setPendingTotal(p.data.count || 0);
      setAllUsers(u.data.users || []);
      setAudit(a.data.audit || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAction = async (u, action, payload) => {
    setBusyId(u.id);
    setError(null);
    try {
      const fn = adminApi[action];
      await fn(u.id, payload);
      setModal(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const ACTION_CONFIG = {
    approve:    { title: "Approve user",    prompt: "Optional note for the audit log.",        requireReason: false },
    reject:     { title: "Reject user",     prompt: "Why is this user being rejected?",        requireReason: true },
    suspend:    { title: "Suspend user",    prompt: "Why are you suspending this user?",       requireReason: true },
    reactivate: { title: "Reactivate user", prompt: "Optional note.",                          requireReason: false },
    promote:    { title: "Promote to admin",prompt: "Optional note.",                          requireReason: false },
    demote:     { title: "Demote from admin", prompt: "Optional note.",                        requireReason: false },
  };

  const renderUserRow = (u) => {
    const sb = STATUS_BADGE[u.status] || STATUS_BADGE.active;
    const rb = ROLE_BADGE[u.role] || ROLE_BADGE.user;
    const RIcon = rb.icon;
    const isSelf = u.id === user.id;
    return (
      <Card key={u.id} className="bg-slate-900/40 border-slate-800/50" data-testid={`admin-user-row-${u.id}`}>
        <CardContent className="p-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-200" data-testid={`admin-user-email-${u.id}`}>{u.email}</span>
              <Badge className={`text-[10px] border ${sb.cls}`} data-testid={`admin-user-status-${u.id}`}>{sb.label}</Badge>
              <Badge className={`text-[10px] border gap-1 ${rb.cls}`}><RIcon className="w-2.5 h-2.5" />{rb.label}</Badge>
              {isSelf && <Badge className="text-[10px] bg-slate-800 text-slate-400 border-none">you</Badge>}
            </div>
            <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
              {u.full_name && <span>{u.full_name}</span>}
              {u.org_id && <span className="font-mono">org: {u.org_id.slice(0, 8)}</span>}
              {u.created_at && <span>signed up {new Date(u.created_at).toLocaleDateString("en-GB")}</span>}
              {u.last_login_at && <span>last seen {new Date(u.last_login_at).toLocaleDateString("en-GB")}</span>}
              {u.rejection_reason && <span className="text-red-400">rejected: {u.rejection_reason}</span>}
              {u.suspension_reason && <span className="text-orange-400">suspended: {u.suspension_reason}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {u.status === "pending_approval" && (
              <>
                <Button size="sm" disabled={busyId === u.id} onClick={() => setModal({ user: u, action: "approve" })}
                  className="bg-emerald-600 hover:bg-emerald-500 gap-1 h-8" data-testid={`admin-approve-${u.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" disabled={busyId === u.id} onClick={() => setModal({ user: u, action: "reject" })}
                  className="border-red-500/30 text-red-300 hover:bg-red-500/10 gap-1 h-8" data-testid={`admin-reject-${u.id}`}>
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </Button>
              </>
            )}
            {u.status === "active" && !isSelf && (
              <Button size="sm" variant="outline" disabled={busyId === u.id} onClick={() => setModal({ user: u, action: "suspend" })}
                className="border-orange-500/30 text-orange-300 hover:bg-orange-500/10 gap-1 h-8" data-testid={`admin-suspend-${u.id}`}>
                <Pause className="w-3.5 h-3.5" /> Suspend
              </Button>
            )}
            {(u.status === "suspended" || u.status === "rejected") && (
              <Button size="sm" disabled={busyId === u.id} onClick={() => setModal({ user: u, action: "reactivate" })}
                className="bg-cyan-600 hover:bg-cyan-500 gap-1 h-8" data-testid={`admin-reactivate-${u.id}`}>
                <Play className="w-3.5 h-3.5" /> Reactivate
              </Button>
            )}
            {u.status === "active" && u.role !== "admin" && (
              <Button size="sm" variant="outline" disabled={busyId === u.id} onClick={() => setModal({ user: u, action: "promote" })}
                className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 gap-1 h-8" data-testid={`admin-promote-${u.id}`}>
                <ArrowUpCircle className="w-3.5 h-3.5" /> Promote
              </Button>
            )}
            {u.role === "admin" && (
              <Button size="sm" variant="outline" disabled={busyId === u.id} onClick={() => setModal({ user: u, action: "demote" })}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 gap-1 h-8" data-testid={`admin-demote-${u.id}`}>
                <ArrowDownCircle className="w-3.5 h-3.5" /> Demote
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-cyan-400" />
            <div>
              <h1 className="text-2xl font-bold text-slate-100">Admin Console</h1>
              <p className="text-xs text-slate-500">Platform-level user approval and audit.</p>
            </div>
          </div>
          <Button variant="outline" onClick={refresh} disabled={loading} className="border-slate-700 text-slate-300 gap-2" data-testid="admin-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </Button>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="admin-error">
            {String(error)}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-800/50 pb-2">
          {[
            { key: "pending", label: `Pending Approvals${pendingTotal > 0 ? ` (${pendingTotal})` : ""}`, icon: Clock, highlight: pendingTotal > 0 },
            { key: "users",   label: "All Users", icon: Users, highlight: false },
            { key: "audit",   label: "Audit Log", icon: ScrollText, highlight: false },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors ${
                  tab === t.key ? "bg-slate-800 text-slate-100" :
                  t.highlight ? "bg-amber-500/10 text-amber-300 border border-amber-500/20" :
                  "text-slate-500 hover:text-slate-300"
                }`}
                data-testid={`admin-tab-${t.key}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>

        {/* PENDING */}
        {tab === "pending" && (
          <div className="space-y-3" data-testid="admin-pending-list">
            {pending.length === 0 && (
              <Card className="bg-slate-900/40 border-slate-800/50">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500/50 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No users awaiting approval.</p>
                </CardContent>
              </Card>
            )}
            {pending.map(renderUserRow)}
          </div>
        )}

        {/* ALL USERS */}
        {tab === "users" && (
          <div className="space-y-3" data-testid="admin-users-list">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name…"
                  className="pl-9 bg-slate-900 border-slate-700 text-sm" data-testid="admin-user-search" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-xs text-slate-300" data-testid="admin-status-filter">
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="pending_approval">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            {allUsers.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No users match.</p>}
            {allUsers.map(renderUserRow)}
          </div>
        )}

        {/* AUDIT */}
        {tab === "audit" && (
          <div className="space-y-1" data-testid="admin-audit-list">
            {audit.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No audit events yet.</p>}
            {audit.map((a, i) => {
              const ICONS = { approve: ShieldCheck, reject: ShieldX, suspend: ShieldAlert,
                              reactivate: Play, promote: ArrowUpCircle, demote: ArrowDownCircle };
              const Icon = ICONS[a.action] || Clock;
              const COLORS = {
                approve: "text-emerald-400", reject: "text-red-400",
                suspend: "text-orange-400", reactivate: "text-cyan-400",
                promote: "text-cyan-400", demote: "text-slate-400",
              };
              return (
                <div key={i} className="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-slate-900/40" data-testid={`admin-audit-row-${i}`}>
                  <Icon className={`w-4 h-4 mt-0.5 ${COLORS[a.action] || "text-slate-400"}`} />
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="text-slate-300">
                      <span className="font-mono text-slate-500">{a.actor_email || a.actor_id?.slice(0, 8)}</span>
                      {" "}<span className={`font-semibold ${COLORS[a.action] || "text-slate-300"}`}>{a.action}</span>{" "}
                      <span className="font-mono text-slate-500">{a.target_user_id?.slice(0, 8)}</span>
                      {a.reason && <span className="text-slate-500"> — {a.reason}</span>}
                    </p>
                    <p className="text-[10px] text-slate-600 mt-0.5">
                      {new Date(a.timestamp).toLocaleString("en-GB")}{a.ip ? ` · ${a.ip}` : ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reason / note modal */}
      <ReasonModal
        open={!!modal}
        title={modal ? ACTION_CONFIG[modal.action].title : ""}
        prompt={modal ? `${ACTION_CONFIG[modal.action].prompt} — Target: ${modal.user.email}` : ""}
        requireReason={modal ? ACTION_CONFIG[modal.action].requireReason : false}
        onClose={() => setModal(null)}
        busy={busyId === modal?.user?.id}
        onSubmit={(text) => {
          if (!modal) return;
          const cfg = ACTION_CONFIG[modal.action];
          const payload = cfg.requireReason ? text : (text || "");
          handleAction(modal.user, modal.action, payload);
        }}
      />
    </div>
  );
}
